import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';

interface ModulePermissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  hasFullAccess: boolean;
  hasAccess: boolean;
}

interface UsePermissionsReturn {
  hasPermission: (moduleName: string, permission: string) => boolean;
  getModulePermissions: (moduleName: string) => ModulePermissions;
  canAccessModule: (moduleName: string) => boolean;
  loading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
}

export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  const { currentBranch } = useBranch();
  const [permissions, setPermissions] = useState<{ [key: string]: ModulePermissions }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    if (!user || !currentBranch) return;

    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      
      const headers: { [key: string]: string } = {
        'Authorization': `Bearer ${token}`
      };
      
      // Add branch ID header if we have a current branch
      if (currentBranch && currentBranch.id) {
        headers['X-Branch-Id'] = currentBranch.id.toString();
      }
      
      const response = await fetch(`${API_BASE_URL}/role-management/my-modules`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        
        // Convert the modules data to our permissions format
        const newPermissions: { [key: string]: ModulePermissions } = {};
        
        data.modules.forEach((module: any) => {
          newPermissions[module.name] = {
            canView: module.can_view || false,
            canCreate: module.can_create || false,
            canEdit: module.can_edit || false,
            canDelete: module.can_delete || false,
            hasFullAccess: module.full_access || false,
            hasAccess: !module.no_access
          };
        });
        
        setPermissions(newPermissions);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch permissions');
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [user, currentBranch]);

  const hasPermission = (moduleName: string, permission: string): boolean => {
    // Admins and organization owners have full access to everything
    if (user?.role === 'admin' || user?.isOrgOwner) {
      return true;
    }

    const modulePermissions = permissions[moduleName];
    
    if (!modulePermissions) {
      return false;
    }

    // If no access, return false for all permissions
    if (!modulePermissions.hasAccess) {
      return false;
    }

    // If full access, return true for all permissions
    if (modulePermissions.hasFullAccess) {
      return true;
    }

    // Check specific permission
    let hasSpecificPermission = false;
    switch (permission) {
      case 'view':
        hasSpecificPermission = modulePermissions.canView;
        break;
      case 'create':
        hasSpecificPermission = modulePermissions.canCreate;
        break;
      case 'edit':
        hasSpecificPermission = modulePermissions.canEdit;
        break;
      case 'delete':
        hasSpecificPermission = modulePermissions.canDelete;
        break;
      default:
        hasSpecificPermission = false;
    }
    
    return hasSpecificPermission;
  };

  const getModulePermissions = (moduleName: string): ModulePermissions => {
    return permissions[moduleName] || {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      hasFullAccess: false,
      hasAccess: false
    };
  };

  const canAccessModule = (moduleName: string): boolean => {
    // Admins and organization owners have full access to everything
    if (user?.role === 'admin' || user?.isOrgOwner) {
      return true;
    }

    const modulePermissions = permissions[moduleName];
    return modulePermissions ? modulePermissions.hasAccess : false;
  };

  const refreshPermissions = async () => {
    await fetchPermissions();
  };

  return {
    hasPermission,
    getModulePermissions,
    canAccessModule,
    loading,
    error,
    refreshPermissions
  };
};

export default usePermissions;
